import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  LinearProgress,
  Typography,
  Button,
  Box,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Tooltip,
} from '@mui/material';
import { getJobStatusColor, formatDate, formatDuration } from '../../utils/helpers';
import JobErrorDialog from './JobErrorDialog';

const JobsList = ({ 
    jobs, 
    onCancelJob, 
    onViewResult, 
    onRefresh, 
    loading,
    page = 1,
    pageSize = 10,
    total = 0,
    onPageChange,
}) => {
    const [orderBy, setOrderBy] = useState('created_at');
    const [order, setOrder] = useState('desc'); // 'asc' or 'desc'
    const [filterApiKey, setFilterApiKey] = useState('all');
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [selectedErrorJob, setSelectedErrorJob] = useState(null);

    // Získat unikátní API klíče z jobů
    const availableApiKeys = useMemo(() => {
        const keys = new Set();
        jobs.forEach(job => {
            if (job.api_key_name) {
                keys.add(job.api_key_name);
            }
        });
        return Array.from(keys).sort();
    }, [jobs]);

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filteredAndSortedJobs = useMemo(() => {
        if (!jobs || jobs.length === 0) return [];

        // Filtrovat podle API klíče
        let filtered = jobs;
        if (filterApiKey !== 'all') {
            filtered = filtered.filter(job => job.api_key_name === filterApiKey);
        }

        // Řadit
        const sorted = [...filtered].sort((a, b) => {
            let aValue, bValue;

            switch (orderBy) {
                case 'id':
                    aValue = a.id || '';
                    bValue = b.id || '';
                    return aValue.localeCompare(bValue);
                
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    return aValue.localeCompare(bValue);
                
                case 'progress':
                    aValue = a.progress || 0;
                    bValue = b.progress || 0;
                    return aValue - bValue;
                
                case 'created_at':
                    aValue = new Date(a.created_at || 0).getTime();
                    bValue = new Date(b.created_at || 0).getTime();
                    return aValue - bValue;
                
                case 'started_at':
                    aValue = new Date(a.started_at || 0).getTime();
                    bValue = new Date(b.started_at || 0).getTime();
                    return aValue - bValue;
                
                case 'completed_at':
                    aValue = new Date(a.completed_at || 0).getTime();
                    bValue = new Date(b.completed_at || 0).getTime();
                    return aValue - bValue;
                
                case 'duration':
                    aValue = a.actual_duration || 0;
                    bValue = b.actual_duration || 0;
                    return aValue - bValue;
                
                case 'api_key_name':
                    aValue = a.api_key_name || '';
                    bValue = b.api_key_name || '';
                    return aValue.localeCompare(bValue);
                
                default:
                    return 0;
            }
        });

        return order === 'asc' ? sorted : sorted.reverse();
    }, [jobs, filterApiKey, orderBy, order]);

    const handleViewError = (job) => {
        setSelectedErrorJob(job);
        setErrorDialogOpen(true);
    };

    const handleCloseErrorDialog = () => {
        setErrorDialogOpen(false);
        setSelectedErrorJob(null);
    };

    return (
        <>
        <Paper elevation={1} data-test-id="jobs-list">
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Recent Jobs
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              {availableApiKeys.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by API Key</InputLabel>
                  <Select
                    value={filterApiKey}
                    label="Filter by API Key"
                    onChange={(e) => setFilterApiKey(e.target.value)}
                    inputProps={{ 'data-test-id': 'jobs-filter-api-key' }}
                  >
                    <MenuItem value="all">All API Keys</MenuItem>
                    {availableApiKeys.map(key => (
                      <MenuItem key={key} value={key}>{key}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Button
                variant="outlined"
                onClick={onRefresh}
                disabled={loading}
                data-test-id="jobs-refresh-btn"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </Box>
          </Box>

          <TableContainer 
            sx={{ 
              maxHeight: 'calc(100vh - 300px)',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                },
              },
            }}
          >
            <Table stickyHeader data-test-id="jobs-table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'progress'}
                      direction={orderBy === 'progress' ? order : 'asc'}
                      onClick={() => handleSort('progress')}
                    >
                      Progress
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'duration'}
                      direction={orderBy === 'duration' ? order : 'asc'}
                      onClick={() => handleSort('duration')}
                    >
                      Duration
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'created_at'}
                      direction={orderBy === 'created_at' ? order : 'asc'}
                      onClick={() => handleSort('created_at')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'started_at'}
                      direction={orderBy === 'started_at' ? order : 'asc'}
                      onClick={() => handleSort('started_at')}
                    >
                      Started
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'completed_at'}
                      direction={orderBy === 'completed_at' ? order : 'asc'}
                      onClick={() => handleSort('completed_at')}
                    >
                      Completed
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'api_key_name'}
                      direction={orderBy === 'api_key_name' ? order : 'asc'}
                      onClick={() => handleSort('api_key_name')}
                    >
                      API Key
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedJobs.map((job) => (
                  <TableRow key={job.id} hover data-test-id={`job-row-${job.id}`}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {job.id}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.status}
                        color={getJobStatusColor(job.status)}
                        size="small"
                        data-test-id={`job-status-${job.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {job.status === 'pending' && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => onCancelJob(job.id)}
                            data-test-id={`job-cancel-btn-${job.id}`}
                          >
                            Cancel
                          </Button>
                        )}
                        {job.status === 'processing' && (
                          <Tooltip title="Cannot cancel job that is already processing." arrow>
                            <span>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled
                              >
                                Cancel
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {job.status === 'completed' && job.result_data?.transcription && (
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            onClick={() => onViewResult(job)}
                            data-test-id={`job-view-result-btn-${job.id}`}
                          >
                            View Result
                          </Button>
                        )}
                        {job.status === 'failed' && job.error_message && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleViewError(job)}
                            data-test-id={`job-view-error-btn-${job.id}`}
                          >
                            View Error
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {job.upload_progress !== undefined && job.upload_progress < 100 ? (
                        // Show upload progress if upload is not complete
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={job.upload_progress || 0}
                              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                              color="secondary"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {job.upload_progress || 0}%
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Uploading...
                          </Typography>
                        </Box>
                      ) : job.status === 'processing' ? (
                        // Show processing progress if upload is complete and job is processing
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={job.progress || 0}
                              sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                              color="primary"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {job.progress || 0}%
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            Processing...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.actual_duration ? formatDuration(job.actual_duration) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(job.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.started_at ? formatDate(job.started_at) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.completed_at ? formatDate(job.completed_at) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {job.api_key_name ? (
                        <Chip 
                          label={job.api_key_name}
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredAndSortedJobs.length === 0 && !loading && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {filterApiKey !== 'all' 
                    ? `No jobs found for API key "${filterApiKey}".` 
                    : 'No jobs found. Create your first transcription job above.'}
                </Typography>
              </Box>
            )}
          </TableContainer>
          
          {total > 0 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} jobs
              </Typography>
              <Pagination
                count={Math.ceil(total / pageSize)}
                page={page}
                onChange={(event, value) => onPageChange && onPageChange(value)}
                color="primary"
                showFirstButton
                showLastButton
                data-test-id="jobs-pagination"
              />
            </Box>
          )}
        </Paper>
        
        <JobErrorDialog
          open={errorDialogOpen}
          onClose={handleCloseErrorDialog}
          job={selectedErrorJob}
        />
        </>
    );
};

export default JobsList;
